import { Form, Modal, Spinner } from "react-bootstrap";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import ButtonLoginWithGoogle from "../ButtonLoginWithGoogle";
import GlobalContext from "../../context/GlobalContext";
import { register as registerApi } from "../../api/handleRegisterApi";
import { loadTermsConditions } from "../../api/TermsConditionsApi";
import swal from "../../services/swalConfig";
import { useTranslation } from "react-i18next";

const AuthRegisterForm = ({
  onRequireLogin,
  onSuccess,
  showGoogle = true,
  registerSource = "console",
}) => {
  const {
    register: registerField,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm();
  const { setLoading } = useContext(GlobalContext);
  const { t } = useTranslation(["auth", "common"]);
  const [showPassword, setShowPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [isTermsLoading, setIsTermsLoading] = useState(false);

  const handleOpenTerms = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowTermsModal(true);

    if (termsContent) {
      return;
    }

    setIsTermsLoading(true);
    try {
      const response = await loadTermsConditions();
      if (response?.status === 200) {
        setTermsContent(response.data || "");
      }
    } catch (error) {
      setTermsContent("");
    } finally {
      setIsTermsLoading(false);
    }
  };

  const submitHandler = async (data) => {
    setLoading(true);

    try {
      const response = await registerApi({
        ...data,
        register_source: registerSource,
      });

      if (response?.status === 200) {
        if (onSuccess) {
          onSuccess(response);
        }
        return;
      }

      if (response?.status === 400 && response?.errors) {
        const errorData = response.errors;
        Object.keys(errorData).forEach((key) => {
          setError(key, {
            type: "manual",
            message: errorData[key],
          });
        });
        return;
      }

      swal.fire({
        title: t("common:errors.oops"),
        text: response?.message || t("common:errors.register_failed"),
        icon: "error",
        showCancelButton: false,
      });
    } catch (error) {
      swal.fire({
        title: t("common:errors.oops"),
        text: t("common:errors.register_failed"),
        icon: "error",
        showCancelButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form
        className="modal-login-popup"
        onSubmit={handleSubmit(submitHandler)}
      >
        <Form.Group className="mb-3" controlId="auth-register-email">
          <Form.Label>{t("login.email")}</Form.Label>
          <Form.Control
            {...registerField("email", {
              required: t("validation.email_required"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("validation.email_invalid"),
              },
            })}
            type="email"
            placeholder={t("login.email_placeholder")}
          />
          {errors.email && (
            <small className="text-danger">{errors.email.message}</small>
          )}
        </Form.Group>

        <div className="row">
          <Form.Group
            className="mb-3 col-6"
            controlId="auth-register-firstname"
          >
            <Form.Label>{t("register.first_name")}</Form.Label>
            <Form.Control
              {...registerField("firstName", {
                required: t("validation.first_name_required"),
                validate: (value) =>
                  !value.includes(" ") || t("validation.first_name_spaces"),
              })}
              type="text"
              placeholder={t("register.first_name_placeholder")}
            />
            {errors.firstName && (
              <small className="text-danger">{errors.firstName.message}</small>
            )}
          </Form.Group>

          <Form.Group className="mb-3 col-6" controlId="auth-register-lastname">
            <Form.Label>{t("register.last_name")}</Form.Label>
            <Form.Control
              {...registerField("lastName", {
                required: t("validation.last_name_required"),
                validate: (value) =>
                  !value.includes(" ") || t("validation.last_name_spaces"),
              })}
              type="text"
              placeholder={t("register.last_name_placeholder")}
            />
            {errors.lastName && (
              <small className="text-danger">{errors.lastName.message}</small>
            )}
          </Form.Group>
        </div>

        <Form.Group className="mb-3" controlId="auth-register-password">
          <Form.Label>{t("login.password")}</Form.Label>
          <div className="input-group">
            <Form.Control
              {...registerField("password", {
                required: t("validation.password_required"),
                validate: (value) =>
                  value === watch("password2") || t("validation.password_mismatch"),
              })}
              type={showPassword ? "text" : "password"}
              placeholder={t("register.password_placeholder")}
            />
            <button
              type="button"
              className="btn btn-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? t("register.hide") : t("register.show")}
            </button>
          </div>
          {errors.password && (
            <small className="text-danger">{errors.password.message}</small>
          )}
        </Form.Group>

        <Form.Group className="mb-3" controlId="auth-register-password2">
          <Form.Label>{t("register.password_confirm")}</Form.Label>
          <div className="input-group">
            <Form.Control
              {...registerField("password2", {
                required: t("validation.password_confirm_required"),
                validate: (value) =>
                  value === watch("password") || t("validation.password_mismatch"),
              })}
              type={showPassword ? "text" : "password"}
              placeholder={t("register.password_confirm_placeholder")}
            />
            <button
              type="button"
              className="btn btn-password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? t("register.hide") : t("register.show")}
            </button>
          </div>
          {errors.password2 && (
            <small className="text-danger">{errors.password2.message}</small>
          )}
        </Form.Group>

        <Form.Group className="mb-3" controlId="auth-register-terms">
          <Form.Check
            {...registerField("terms_conditions", {
              required: t("validation.terms_required"),
            })}
            type="checkbox"
            label={
              <span>
                {t("register.terms_prefix")}{" "}
                <button
                  type="button"
                  className="btn-link p-0 border-0 register-terms-link"
                  onClick={handleOpenTerms}
                >
                  {t("register.terms_link")}
                </button>
                .
              </span>
            }
          />
          {errors.terms_conditions && (
            <small className="text-danger">
              {errors.terms_conditions.message}
            </small>
          )}
        </Form.Group>

        <div className="text-center">
          <button type="submit" className="w-100 btn-modal-login border-0">
            {t("register.submit")}
          </button>
        </div>
      </Form>

      {showGoogle && (
        <>
          <span className="text-middle-of-line mt-3">{t("or")}</span>
          <div className="text-center mt-3 mb-3">
            <ButtonLoginWithGoogle />
          </div>
        </>
      )}

      {onRequireLogin && (
        <p className="text-center mb-0">
          {t("register.have_account")}{" "}
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={onRequireLogin}
          >
            <b>{t("register.login_link")}</b>
          </button>
        </p>
      )}

      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("register.terms_link")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {isTermsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : termsContent ? (
            <div dangerouslySetInnerHTML={{ __html: termsContent }} />
          ) : (
            <p className="mb-0">{t("register.terms_unavailable")}</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AuthRegisterForm;
